import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import invoiceFilterCategories from 'src/assets/static/action_required_invoices/invoiceFilterCategories.json';
import Cards from 'src/components/Cards';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useActionRequiredInvoices from 'src/hooks/useActionRequiredInvoices';
import useConfirm from 'src/hooks/useConfirm';
import SubmitForApprovalDialog from 'src/sections/action_required_invoices/SubmitForApprovalDialog';
import { IItemsActionRequired } from 'src/services/useActionRequiredInvoicesApi';
import useApproveInvoiceApi from 'src/services/useApproveInvoiceApi';
import useDeclineInvoiceApi from 'src/services/useDeclineApi';
import PermissionGuard from '../../components/guards/PermissionGuard';
import {
  CanSubmitInvoicesCompanyPermissions,
  CompleteInvoicesCompanyPermissions,
} from '../../components/guards/PermissionsList';

interface IErrorComponent {
  header: string;
  errorListIds: string[];
  numType: string;
}

export const ErrorComponent = ({ header, errorListIds, numType }: IErrorComponent) => (
  <Cards title={`${header}`} styleType="error">
    <p className="text-left" style={{ margin: '0px' }}>
      The records below require additional approval. <br />
      Please select those and click "Submit for approval"
    </p>
    <ul>
      {errorListIds.map((errorId, keyIndex) => (
        <li key={keyIndex + 1} style={{ wordWrap: 'break-word' }}>
          {numType}#: <b>{errorId}</b>
        </li>
      ))}
    </ul>
  </Cards>
);

const ActionRequiredTools = () => {
  const {
    actionRequiredSelectionFilter,
    setActionRequiredSelectionFilter,
    selectedInvoices,
    actionRequiredGridRefresh,
  } = useActionRequiredInvoices();
  const selectedInvoiceIds = useMemo(
    () =>
      selectedInvoices.map((row: IItemsActionRequired) =>
        row.receiptId ? row?.receiptId?.toString() : ''
      ),
    [selectedInvoices]
  );
  const { confirm } = useConfirm();
  const { approveInvoice, status: completeStatus, approveCallLoading } = useApproveInvoiceApi();
  const { declineInvoice, status: declineStatus, declineCallLoading } = useDeclineInvoiceApi();

  const { isApproveDisabled, invalidApproveIds } = useMemo(() => {
    let isApproveDisabled = true;
    let invalidApproveItems: IItemsActionRequired[] = [];
    if (selectedInvoices?.length > 0) {
      isApproveDisabled = !selectedInvoices.every((invoice) => invoice?.canApproveInvoice);
      invalidApproveItems = selectedInvoices.filter(
        (invoice) => !invoice?.canApproveInvoice && !invoice?.requireFurtherApproval
      );
    }
    return {
      isApproveDisabled,
      invalidApproveIds: invalidApproveItems.map((invoice) => invoice?.invoiceNumber),
    };
  }, [selectedInvoices]);

  const { isSubmitForApprovalDisabled, invalidSubmitForApprovalIds } = useMemo(() => {
    let isSubmitForApprovalDisabled = true;
    let invalidSubmitForApprovalItems: IItemsActionRequired[] = [];
    if (selectedInvoices?.length > 0) {
      isSubmitForApprovalDisabled = !selectedInvoices.every(
        (invoice) => invoice?.canSubmitInvoiceForApproval
      );
      invalidSubmitForApprovalItems = selectedInvoices.filter(
        (invoice) => !invoice?.canSubmitInvoiceForApproval
      );
    }
    return {
      isSubmitForApprovalDisabled,
      invalidSubmitForApprovalIds: invalidSubmitForApprovalItems.map(
        (invoice) => invoice?.invoiceNumber
      ),
    };
  }, [selectedInvoices]);

  const { canInvoiceFurtherApproval, invoiceFurtherApprovalIds } = useMemo(() => {
    let canInvoiceFurtherApproval = false;
    let invoiceFurtherApprovalItems: IItemsActionRequired[] = [];
    if (selectedInvoices?.length > 0) {
      canInvoiceFurtherApproval = selectedInvoices.every(
        (invoice) => invoice?.requireFurtherApproval
      );
      invoiceFurtherApprovalItems = selectedInvoices.filter(
        (invoice) => invoice?.requireFurtherApproval
      );
    }
    return {
      canInvoiceFurtherApproval,
      invoiceFurtherApprovalIds: invoiceFurtherApprovalItems.map(
        (invoice) => invoice?.invoiceNumber
      ),
    };
  }, [selectedInvoices]);

  const { isDeclineDisabled } = useMemo(() => {
    let isDeclineDisabled = true;
    if (selectedInvoices?.length > 0) {
      isDeclineDisabled = !selectedInvoices.every((invoice) => invoice?.canDeclineInvoice);
    }
    return { isDeclineDisabled };
  }, [selectedInvoices]);

  const handleDecline = useCallback(() => {
    confirm({
      title: 'Decline Document',
      content: 'Are you sure you want to decline the selected item(s) ?',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Yes, decline',
      className: 'actionRequiredModalRed',
    })
      .then(async (isDeclineClicked) => {
        if (isDeclineClicked) {
          try {
            await declineInvoice({ invoiceIds: selectedInvoiceIds });
            if (declineStatus === 'success') {
              toast.success('Document(s) declined successfully');
            }
            actionRequiredGridRefresh();
          } catch (e) {
            console.error(e);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, [actionRequiredGridRefresh, confirm, declineInvoice, declineStatus, selectedInvoiceIds]);

  useEffect(() => {
    if (completeStatus === 'success') {
      toast.success('Document(s) completed successfully');
    }
  }, [completeStatus]);

  const handleApprove = useCallback(() => {
    confirm({
      title: 'Approve Document',
      content: 'Are you sure you want to approve the selected item(s) ?',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
      confirmButtonText: 'Yes, approve',
      confirmColor: 'success',
      className: 'actionRequiredModalGreen',
    })
      .then(async (isApproveClicked) => {
        if (isApproveClicked) {
          try {
            await approveInvoice({ invoiceIds: selectedInvoiceIds });
            actionRequiredGridRefresh();
          } catch (e) {
            console.error(e);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }, [actionRequiredGridRefresh, approveInvoice, confirm, selectedInvoiceIds]);

  return (
    <div className="flex justify-between items-center content-around flex-wrap">
      <p className="font-semibold text-sm mr-4">
        Invoices/credits that require approval for payment
      </p>
      <div id="action-required-btns" className="flex justify-between items-center flex-wrap">
        <PopUp
          key="approve"
          content={
            <div className="max-h-[30vh] overflow-auto">
              <PopUpContentWrapper
                contents={invalidApproveIds.map((i) => `# ${i}`)}
                type="Error"
                prefix={'Required Approval'}
                title="The document(s) listed below have errors that are preventing them from being approved. Please open, correct, and individually approve."
              />
              <PopUpContentWrapper
                contents={invoiceFurtherApprovalIds.map((i) => `# ${i}`)}
                type="Information"
                title="The document(s) listed below require further approval. Please select those records and submit for approval"
              />
            </div>
          }
        >
          <PermissionGuard permissions={CompleteInvoicesCompanyPermissions} or>
            <Button
              color="success"
              onClick={handleApprove}
              className={`mr-4`}
              disabled={isApproveDisabled || canInvoiceFurtherApproval}
              loading={approveCallLoading}
            >
              Approve
            </Button>
          </PermissionGuard>
        </PopUp>
        <PopUp
          key="submitForApproval"
          content={
            <div className="max-h-[30vh] overflow-auto">
              <PopUpContentWrapper
                contents={invalidSubmitForApprovalIds.map((i) => `# ${i}`)}
                type="Error"
                prefix={'Submit For Approval'}
                title="The document(s) listed below have errors that are preventing them from being submitted for approval. Please open, correct, and individually submit for approval."
              />
            </div>
          }
        >
          <PermissionGuard permissions={CanSubmitInvoicesCompanyPermissions} or>
            <SubmitForApprovalDialog
              btnColor="submit"
              className={`mr-4 whitespace-nowrap ${
                isSubmitForApprovalDisabled ? '' : 'text-black'
              }`}
              btnDisabled={isSubmitForApprovalDisabled}
              invoiceIds={selectedInvoiceIds}
              refresh={actionRequiredGridRefresh}
            />
          </PermissionGuard>
        </PopUp>
        <Button
          color="error"
          onClick={handleDecline}
          className="mr-4"
          disabled={isDeclineDisabled}
          loading={declineCallLoading}
        >
          Decline
        </Button>
      </div>
      <DropDownList
        id="action-required-grid-dropdown"
        style={{ width: 300 }}
        data={invoiceFilterCategories}
        textField="label"
        dataItemKey="value"
        value={actionRequiredSelectionFilter}
        onChange={(e) => setActionRequiredSelectionFilter(e.target.value)}
      />
    </div>
  );
};

export default ActionRequiredTools;
